import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { isPlatformAdminRole } from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiMail, FiFileText, FiSettings, FiUser, FiUsers, FiEdit3, FiTool, FiX, FiBriefcase, FiLifeBuoy, FiVideo, FiLifeBuoy: FiHelp } = FiIcons;

const Sidebar = ({ isOpen, onClose }) => {
  const { currentUserRole, currentImpersonatedTenantId } = useAuth();
  const isAdmin = isPlatformAdminRole(currentUserRole);
  const isImpersonating = !!currentImpersonatedTenantId;

  const clientNavItems = [
    { path: '#/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '#/enquiries', icon: FiMail, label: 'Enquiries' },
    { path: '#/blog', icon: FiFileText, label: 'Blog' },
    { path: '#/settings', icon: FiSettings, label: 'Business Settings' },
    { path: '#/change-requests', icon: FiTool, label: 'Website Change Requests' },
    { path: '#/help', icon: FiHelp, label: 'Help Center' },
    { path: '#/profile', icon: FiUser, label: 'Profile' },
    { path: '#/support', icon: FiLifeBuoy, label: 'Support' }
  ];

  const adminNavItems = [
    { path: '#/admin/tenants', icon: FiBriefcase, label: 'Tenants' },
    { path: '#/admin/users', icon: FiUsers, label: 'Users' },
    { path: '#/admin/requests', icon: FiEdit3, label: 'Change Requests' },
    { path: '#/help', icon: FiHelp, label: 'Help Center' },
    { path: '#/support', icon: FiLifeBuoy, label: 'Support' }
  ];

  const getCurrentPath = () => window.location.hash || '#/dashboard';
  const navItems = isAdmin && !isImpersonating ? adminNavItems : clientNavItems;

  const sidebarVariants = {
    open: { x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
    closed: { x: "-100%", transition: { type: "spring", stiffness: 300, damping: 30 } }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="theme-mobile-overlay fixed inset-0 z-40 lg:hidden" onClick={onClose} />
        )}
      </AnimatePresence>
      <motion.div variants={sidebarVariants} animate={isOpen ? "open" : "closed"} className="theme-sidebar fixed left-0 top-0 h-full w-64 border-r z-50 lg:relative lg:translate-x-0">
        <div className="theme-sidebar-header flex items-center justify-between p-4 border-b lg:hidden">
          <h2 className="theme-sidebar-title text-lg font-semibold">Navigation</h2>
          <button onClick={onClose} className="theme-sidebar-close p-2 rounded-md"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
        </div>
        <nav className="p-4">
          {isAdmin && isImpersonating && (
            <div className="theme-sidebar-mode mb-4 p-3 rounded-md">
              <p className="text-sm font-medium">Client Portal View</p>
              <p className="text-xs">Viewing as impersonated tenant</p>
            </div>
          )}
          {isAdmin && !isImpersonating && (
            <div className="theme-sidebar-mode mb-4 p-3 rounded-md">
              <p className="text-sm font-medium">Admin Mode</p>
              <p className="text-xs">BPA Solutions Platform</p>
            </div>
          )}
          <ul className="space-y-2">
            {navItems.map((item) => {
              const isActive = getCurrentPath() === item.path;
              return (
                <li key={item.path}>
                  <a href={item.path} onClick={onClose} className={`theme-sidebar-link flex items-center space-x-3 px-3 py-2.5 rounded-md transition-colors ${isActive ? 'theme-sidebar-link-active' : ''}`}>
                    <SafeIcon icon={item.icon} className="w-5 h-5" />
                    <span>{item.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </motion.div>
    </>
  );
};

export default Sidebar;